import { Button, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight, TrendingUp } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import type { EarlyFreedomContext } from "web_myplann/Inspiration/Utils/earlyFreedomMath";
import { Callout } from "web_myplann/Shared/Components/Callout";

const R = Resources.Inspiration.EarlyFreedom;

type Props = {
	context: EarlyFreedomContext;
	onEdit: () => void;
};

export const EarlyFreedomPassiveSection: FC<Props> = ({ context, onEdit }) => {
	const { t } = useResource();
	const theme = useTheme();

	if (context.passiveToday <= 0) {
		return (
			<InspirationSection title={t(R.passiveTitle)} gap={1.2}>
				<Typography variant="body2" lineHeight={1.625}>
					{t(R.passiveLiteDescription)}
				</Typography>
				<Callout
					color="blue"
					icon={TrendingUp}
					title={t(R.passiveNudgeTitle)}
					description={t(R.passiveNudgeText)}
					onClick={onEdit}
				/>
			</InspirationSection>
		);
	}

	return (
		<InspirationSection
			title={t(R.passiveTitle)}
			rightAction={
				<Button
					variant="textLink"
					size="small"
					endIcon={<ChevronRight size={14} />}
					onClick={onEdit}
				>
					{t(R.edit)}
				</Button>
			}
		>
			<Stack
				bgcolor={theme.palette.surface.muted}
				borderRadius={theme.radii.lg}
				px={1.6}
				py={1.2}
			>
				<Typography variant="button" color={theme.palette.grey[500]}>
					{t(R.passiveFullLabel)}
				</Typography>
				<Typography
					variant="h1"
					color={theme.palette.green.main}
					tabular
					mt={0.4}
				>
					{formatCurrency(context.passiveToday, INSPIRATION_CURRENCY, 0)}
					<Typography
						component="span"
						variant="bodyMedium"
						color="inherit"
						sx={{ opacity: 0.7 }}
					>
						{t(R.perMonth)}
					</Typography>
				</Typography>
			</Stack>
		</InspirationSection>
	);
};
